import { useEffect, useState, type ReactElement } from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SignalWaitDot } from "@/motion/SignalWaitDot";
import { signal } from "@/motion/signals";

import type { ChatEmbed } from "../../chat/types";
import { startTrial } from "../lib/start-trial";
import { MONTHLY_PRICE_USD, TRIAL_DAYS, trialEndDate } from "../lib/trial-pricing";

export interface IntakeEmbed extends ChatEmbed {
  kind: "intake_form";
  id?: string;
  businessName?: string;
}

export function isIntakeEmbed(embed: ChatEmbed): embed is IntakeEmbed {
  if (embed.kind !== "intake_form") return false;
  return (
    embed.businessName === undefined || typeof embed.businessName === "string"
  );
}

export function IntakeForm({
  embed,
  fieldId,
  onError,
}: {
  embed: IntakeEmbed;
  fieldId: string;
  onError: (error: string) => void;
}): ReactElement {
  const [businessName, setBusinessName] = useState(embed.businessName ?? "");
  const [email, setEmail] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (embed.businessName && !businessName) {
      setBusinessName(embed.businessName);
    }
  }, [embed.businessName, businessName]);

  async function onStart(): Promise<void> {
    const name = businessName.trim();
    const address = email.trim();
    if (!name || !address || starting) return;

    setStarting(true);
    onError("");
    const result = await startTrial({
      businessName: name,
      email: address,
      // Default to monthly here — this is the small, low-commitment first
      // step (research: reduce the work, don't surprise on price). Annual
      // stays an explicit opt-in on /signup, not a silent default.
      cycle: "monthly",
    });
    if (!result.ok) {
      setStarting(false);
      onError(result.error);
      return;
    }
    window.location.assign(result.url);
  }

  return (
    <div
      className={cn(
        signal.arrive,
        "rounded-[1.25rem] border border-[#cfe3dd] bg-white/90 p-4 shadow-[0_12px_32px_rgba(34,73,66,0.08)]",
      )}
    >
      <label className="block text-xs text-[#65736f]" htmlFor={`${fieldId}-name`}>
        Site name
      </label>
      <input
        id={`${fieldId}-name`}
        value={businessName}
        onChange={(event) => setBusinessName(event.currentTarget.value)}
        autoComplete="organization"
        className="mt-1 w-full border-0 border-b border-[#d5e3de] bg-transparent py-1.5 text-sm text-[#15231f] outline-none"
      />
      <label className="mt-3 block text-xs text-[#65736f]" htmlFor={`${fieldId}-email`}>
        Email
      </label>
      <input
        id={`${fieldId}-email`}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.currentTarget.value)}
        autoComplete="email"
        className="mt-1 w-full border-0 border-b border-[#d5e3de] bg-transparent py-1.5 text-sm text-[#15231f] outline-none"
      />
      <Button
        type="button"
        size="lg"
        disabled={starting || !businessName.trim() || !email.trim()}
        onClick={() => void onStart()}
        className="mt-4 h-11 w-full rounded-full bg-[#dceffc] px-6 text-sm text-[#20272b] hover:bg-[#c8e6fa]"
      >
        {starting ? (
          <>
            <SignalWaitDot className="bg-white" />
            Opening secure checkout
          </>
        ) : (
          <>
            Start {TRIAL_DAYS} days free
            <ArrowRight aria-hidden="true" />
          </>
        )}
      </Button>
      <p className="mt-3 text-xs leading-5 text-[#65736f]">
        {TRIAL_DAYS} days free, then ${MONTHLY_PRICE_USD}/month starting{" "}
        {trialEndDate()}. Card details are entered on Stripe, never here.
        Cancel anytime before then and you are not charged.
      </p>
    </div>
  );
}
